import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import color from 'color';

import { DEFAULT_PJ_PRIMARY_COLOR } from '../ConfigVars';

const PrimaryColorLoader = () => {
  const hotelColor = useSelector((state) => state.app?.hotelInformation?.color);

  // Use PAYJIM's default orange if hotel has no color or null
  const displayColor = hotelColor ?? DEFAULT_PJ_PRIMARY_COLOR;

  const setPrimaryColorCustomProperties = useCallback(() => {
    try {
      const primaryColor = color(displayColor);

      // Set the --color-pj-primary-400 custom property for the whole app
      document.documentElement.style.setProperty(
        '--color-pj-primary-400',
        primaryColor,
      );
      // ------------------

      // Set derived colors
      const color100 = color(primaryColor)
        .alpha(1 - 0.9)
        .rgb();
      document.documentElement.style.setProperty(
        '--color-pj-primary-100',
        color100,
      );

      const color200 = color(primaryColor)
        .alpha(1 - 0.8)
        .rgb();
      document.documentElement.style.setProperty(
        '--color-pj-primary-200',
        color200,
      );

      const color300 = color(primaryColor)
        .alpha(1 - 0.5)
        .rgb();
      document.documentElement.style.setProperty(
        '--color-pj-primary-300',
        color300,
      );

      const color700 = color(primaryColor).darken(0.6).hex();
      document.documentElement.style.setProperty(
        '--color-pj-primary-700',
        color700,
      );
      // ------------------

      // Compute and set contrasting color (black or white text) for primary color
      const white = color('#ffffff');
      const black = color('#000000');

      // Define a threshold for good contrast (WCAG recommendation is 4.5:1)
      const contrastThreshold = 4.5;

      const blackContrast = color(primaryColor).contrast(black);
      const whiteContrast = color(primaryColor).contrast(white);

      if (blackContrast > whiteContrast * contrastThreshold) {
        document.documentElement.style.setProperty(
          '--color-good-contrast',
          black,
        );
      } else {
        document.documentElement.style.setProperty(
          '--color-good-contrast',
          white,
        );
      }

      // Neutral colors
      document.documentElement.style.setProperty(
        '--color-pj-gray-300',
        '#fbfafa',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-400',
        '#f3f3f3',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-500',
        '#e1e3e3',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-600',
        '#c0c0c0',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-700',
        '#828282',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-750',
        '#303030',
      );
      document.documentElement.style.setProperty(
        '--color-pj-gray-800',
        '#1d1817',
      );
    } catch (error) {
      console.error(`Invalid primary color returned from API: ${displayColor}`);
      console.error(error);
    }
  }, [displayColor]);

  useEffect(() => {
    setPrimaryColorCustomProperties(displayColor);
  }, [displayColor, setPrimaryColorCustomProperties]);

  return null;
};

export default PrimaryColorLoader;
